import { createId } from "@paralleldrive/cuid2"
import {
  boolean,
  foreignKey,
  index,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core"
import { user } from "./auth-schema"

// =============================================================================
// Enums
// =============================================================================

/** Supported flag value types */
export const valueTypeEnum = pgEnum("flag_value_type", ["boolean", "string", "number"])

/** Deployment environments */
export const environmentEnum = pgEnum("flag_environment", ["dev", "staging", "production"])

/** Attributes that can be used for percentage rollout bucketing */
export const rolloutAttributeEnum = pgEnum("rollout_attribute", ["userId", "countryId", "storeId"])

/** Change log actions */
export const flagChangeActionEnum = pgEnum("flag_change_action", [
  "created",
  "updated",
  "archived",
  "override_added",
  "override_removed",
])

// =============================================================================
// Types for JSONB columns
// =============================================================================

/** User roles for targeting rules */
export type UserRole =
  | "admin"
  | "blueprint_planner"
  | "blueprint_reviewer"
  | "store_designer"
  | "store_manager"
  | "external_viewer"

/** A targeting rule stored in JSONB */
export type TargetingRuleJson = {
  role: UserRole
  value: boolean | string | number
  description?: string
}

/** Rollout configuration stored in JSONB */
export type RolloutConfigJson = {
  percentage: number
  attribute: "userId" | "countryId" | "storeId"
  value: boolean | string | number
}

// =============================================================================
// Tables
// =============================================================================

/**
 * Master feature flag definitions.
 * One row per flag, environment-agnostic.
 */
export const featureFlags = pgTable(
  "feature_flags",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    /** Unique key used in code (e.g., "new-dashboard") */
    key: text().notNull().unique(),
    /** Human-readable name */
    name: text().notNull(),
    /** Optional description */
    description: text(),
    /** The type of value this flag returns */
    valueType: valueTypeEnum("value_type").notNull(),
    /** ID of user who created the flag (nullable for ON DELETE SET NULL) */
    createdById: text("created_by_id"),
    /** Creation timestamp */
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    /** Last update timestamp */
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
    /** Soft delete timestamp (null if active) */
    archivedAt: timestamp("archived_at", { withTimezone: true }),
  },
  (table) => [
    foreignKey({
      columns: [table.createdById],
      foreignColumns: [user.id],
      name: "feature_flags_created_by_id_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("feature_flags_key_idx").on(table.key),
    index("feature_flags_created_by_id_idx").on(table.createdById),
    index("feature_flags_archived_at_idx").on(table.archivedAt),
  ],
)

/**
 * Environment-specific flag configuration.
 * One row per flag per environment (dev, staging, production).
 */
export const flagEnvironments = pgTable(
  "flag_environments",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    /** Reference to the parent flag */
    flagId: text("flag_id").notNull(),
    /** Which environment this config applies to */
    environment: environmentEnum().notNull(),
    /** Whether the flag is enabled in this environment */
    enabled: boolean().notNull().default(false),
    /**
     * Default value when no rules match.
     * Stored as JSONB to support boolean/string/number.
     */
    defaultValue: jsonb("default_value").$type<boolean | string | number>().notNull(),
    /** Role-based targeting rules (evaluated in order) */
    targetingRules: jsonb("targeting_rules").$type<TargetingRuleJson[]>().notNull().default([]),
    /** Optional percentage rollout configuration */
    rollout: jsonb().$type<RolloutConfigJson | null>().default(null),
    /** Last update timestamp */
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.flagId],
      foreignColumns: [featureFlags.id],
      name: "flag_environments_flag_id_feature_flags_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    // Each flag can only have one config per environment
    uniqueIndex("flag_environments_flag_id_environment_idx").on(table.flagId, table.environment),
    index("flag_environments_flag_id_idx").on(table.flagId),
    index("flag_environments_environment_idx").on(table.environment),
  ],
)

/**
 * User-specific flag overrides.
 * Takes precedence over all other rules.
 */
export const flagUserOverrides = pgTable(
  "flag_user_overrides",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    /** Reference to the parent flag */
    flagId: text("flag_id").notNull(),
    /** Which environment this override applies to */
    environment: environmentEnum().notNull(),
    /** The user this override applies to */
    userId: text("user_id").notNull(),
    /**
     * The overridden value.
     * Stored as JSONB to support boolean/string/number.
     */
    value: jsonb().$type<boolean | string | number>().notNull(),
    /** Reason for the override */
    reason: text(),
    /** When the override expires (null = never) */
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    /** Creation timestamp */
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.flagId],
      foreignColumns: [featureFlags.id],
      name: "flag_user_overrides_flag_id_feature_flags_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: "flag_user_overrides_user_id_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    // Each user can only have one override per flag per environment
    uniqueIndex("flag_user_overrides_flag_user_env_idx").on(
      table.flagId,
      table.userId,
      table.environment,
    ),
    index("flag_user_overrides_flag_id_idx").on(table.flagId),
    index("flag_user_overrides_user_id_idx").on(table.userId),
    index("flag_user_overrides_expires_at_idx").on(table.expiresAt),
  ],
)

/**
 * Change log for SSE real-time update detection.
 * Records every mutation to flags or overrides.
 */
export const flagChangeLog = pgTable(
  "flag_change_log",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    /** The flag that changed */
    flagId: text("flag_id").notNull(),
    /** Which environment was affected */
    environment: environmentEnum().notNull(),
    /** What action was performed */
    action: flagChangeActionEnum().notNull(),
    /** Who made the change (nullable for ON DELETE SET NULL) */
    changedById: text("changed_by_id"),
    /** When the change occurred */
    changeTimestamp: timestamp("change_timestamp", { withTimezone: true }).defaultNow().notNull(),
    /** JSON diff of what changed */
    diff: jsonb().$type<Record<string, unknown> | null>().default(null),
    /** Additional metadata */
    metadata: jsonb().$type<Record<string, unknown> | null>().default(null),
  },
  (table) => [
    foreignKey({
      columns: [table.flagId],
      foreignColumns: [featureFlags.id],
      name: "flag_change_log_flag_id_feature_flags_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.changedById],
      foreignColumns: [user.id],
      name: "flag_change_log_changed_by_id_user_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    // Index for SSE polling: get changes after a timestamp for an environment
    index("flag_change_log_env_timestamp_idx").on(table.environment, table.changeTimestamp),
    index("flag_change_log_flag_id_idx").on(table.flagId),
    index("flag_change_log_changed_by_id_idx").on(table.changedById),
  ],
)

// =============================================================================
// Type exports for TypeScript inference
// =============================================================================

export type ValueType = (typeof valueTypeEnum.enumValues)[number]
export type Environment = (typeof environmentEnum.enumValues)[number]
export type RolloutAttribute = (typeof rolloutAttributeEnum.enumValues)[number]
export type FlagChangeAction = (typeof flagChangeActionEnum.enumValues)[number]
